/**
 * Lock forms (ท่าล็อกเป้า) in ink: binds the lab's combo samples (showcase.ts) to the real lock moves of
 * packages/shared/src/data/lockForms.ts. Driven by the rules: the form's length, strikes (formStrike + i*0.15, one per
 * `hits`) and receive beats come from shared (duelLock.ts), so each move's blows land where the rules put them. The target's
 * body is the renderer's receive block (lockMark.pose, render/rig/receive.ts). Clone only.
 */
import { DUEL_LOCK, duelLockOf, getSkill } from '@nine/shared';
import type { Entity } from '@nine/shared';
import { LOCK_FORMS as FORM_SAMPLES } from './showcase';
import { drawBlood, setInkHeightK, type InkScene, type InkSample } from './samples';
import { drawImpact, type Weight } from './spectacle';
import { registerRenderPass } from '../passes/registry';
import type { PassFrame } from '../passes/registry';

/** Which combo sample a lock move wears (the art's weapon family). */
const SWORD = new Set(['bs-reed', 'bs-rend', 'ts-flow', 'jy-cross', 'jz-poem', 'fm-cross']);
const sampleOf = (id: string): InkSample => FORM_SAMPLES.find((s) => s.id === (SWORD.has(id) ? 'lock-sword' : 'lock-palm'))!;

interface Run {
  id: string;
  sample: InkSample;
  len: number;
  /** [real seconds, sample seconds] anchors: the real strikes line up with the sample's blows. */
  anchors: [number, number][];
  /** Real strike times, and the sample blow each stands for. */
  strikes: number[];
  blow: number[];
}
const RUNS = new Map<number, Run>();

function runOf(casterId: number, id: string, len: number): Run {
  const cur = RUNS.get(casterId);
  if (cur && cur.id === id && cur.len === len) return cur;
  const s = sampleOf(id);
  const n = Math.max(1, getSkill(id)?.hits ?? 1);
  const m = s.hits.length;
  const strikes: number[] = [];
  const blow: number[] = [];
  for (let i = 0; i < n; i++) {
    const b = n === 1 ? m - 1 : Math.round((i * (m - 1)) / (n - 1));
    if (blow.length && blow[blow.length - 1] === b) continue;
    strikes.push(DUEL_LOCK.formStrike + i * 0.15);
    blow.push(b);
  }
  const anchors: [number, number][] = [[0, 0], ...strikes.map((t, i): [number, number] => [t, s.hits[blow[i]!]!]), [len, s.life]];
  const run = { id, sample: s, len, anchors, strikes, blow };
  RUNS.set(casterId, run);
  return run;
}

const sampleTime = (r: Run, t: number): number => {
  const a = r.anchors;
  for (let i = 1; i < a.length; i++) {
    const [t0, s0] = a[i - 1]!;
    const [t1, s1] = a[i]!;
    if (t <= t1) return s0 + ((s1 - s0) * (t - t0)) / Math.max(1e-6, t1 - t0);
  }
  return a[a.length - 1]![1];
};

interface Live {
  caster: Entity;
  target: Entity;
  run: Run;
  t: number;
}

/** The forms under way, from the rules (the system keeps them private; read-only). */
function lives(f: PassFrame): Live[] {
  const sys = duelLockOf(f.world) as unknown as {
    forms: Map<number, { tgt: number; t: number; len: number; def: unknown }>;
  } | null;
  if (!sys) return [];
  const out: Live[] = [];
  for (const [cid, fm] of sys.forms) {
    const caster = f.world.get(cid);
    const target = f.world.get(fm.tgt);
    if (!caster || !target) continue;
    const id = Object.keys(LOCK_IDS()).find((k) => LOCK_IDS()[k] === fm.def);
    if (!id) continue;
    out.push({ caster, target, run: runOf(cid, id, fm.len), t: fm.t });
  }
  for (const k of [...RUNS.keys()]) if (!sys.forms.has(k)) RUNS.delete(k);
  return out;
}

let table: Record<string, unknown> | null = null;
function LOCK_IDS(): Record<string, unknown> {
  if (!table) {
    table = {};
    // Looked up through the skills: a move whose `lockForm` is its table entry.
    for (const id of [...SWORD, 'cp-wave', 'tf-push', 'ar-strike', 'jl-drain', 'pp-thousand']) {
      const lf = getSkill(id)?.lockForm;
      if (lf) table[id] = lf;
    }
  }
  return table;
}

function sceneOf(f: PassFrame, l: Live): InkScene {
  const [cx, cy] = f.iso.proj(l.caster.x, l.caster.y);
  const [tx, ty] = f.iso.proj(l.target.x, l.target.y);
  const sx = cx + f.ox;
  const sy = cy + f.oy;
  return { x: sx, y: sy, flip: tx + f.ox < sx, D: Math.max(28, Math.abs(tx + f.ox - sx)), Dr: Math.abs(tx + f.ox - sx), tdy: ty + f.oy - sy, guard: false, blood: false };
}

function drawLayer(f: PassFrame, layer: 'ground' | 'over'): void {
  for (const l of lives(f)) {
    const { run } = l;
    const s = run.sample;
    const sc = sceneOf(f, l);
    const st = sampleTime(run, l.t);
    s.draw(f.g, st, sc, layer);
    run.strikes.forEach((_, i) => {
      const h = s.hits[run.blow[i]!]!;
      const last = i === run.strikes.length - 1;
      const w = (last ? 2 : (s.weights?.[run.blow[i]!] ?? 0)) as Weight;
      drawImpact(f.g, st - h, sc, layer, w, s.kind);
      const b = s.bloods?.[run.blow[i]!];
      if (layer === 'over' && b) drawBlood(f.g, st - h, sc, b);
    });
  }
}

// The camera's height factor for every ink/sword effect this frame (render/ink/samples.ts `P`), first thing.
// The figures' factor (`figureK`, lane CAMERA-PITCH): as painted from 30° up, stretched with the heights below.
registerRenderPass({ id: 'inkHeightK', layer: 'ground', order: 3, draw: (f) => setInkHeightK(f.iso.figureK) });
// After the turned ground (turnGroundEnd, 900): live screen coordinates, as the world half.
registerRenderPass({ id: 'lockFormsGround', layer: 'ground', order: 905, draw: (f) => drawLayer(f, 'ground') });
registerRenderPass({ id: 'lockFormsOver', layer: 'world', order: 305, draw: (f) => drawLayer(f, 'over') });
